<template>
  <div>
    <div
      class="h-13 flex"
      @mouseover="hoverState = true"
      @mouseleave="hoverState = false"
    >
      <div class="sticky left-0 z-10 pl-6 flex bg-slate-100">
        <div
          class="w-80 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 justify-between border-l border-r"
          :class="[
            isLast ? 'rounded-bl' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <BlTextBody2
            :title="center.name"
            :text="center.name"
            class="truncate text-slate-1000"
          />
          <BlChip v-if="isCurrentCenter" chip="Current" color="lime" dense />
        </div>
      </div>
      <div
        class="w-40 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlChip :chip="center.status" dense />
      </div>
      <div
        class="w-40 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="center.total_active_members" class="truncate" />
      </div>
      <div
        class="w-40 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="subscriptionPlan"
          :text="subscriptionPlan"
          class="truncate"
        />
      </div>
      <div
        class="w-40 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :title="amountDue" :text="amountDue" class="truncate" />
      </div>
      <div
        class="w-50 px-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="nextBillingDate"
          :text="nextBillingDate"
          class="truncate"
        />
      </div>
      <div
        class="w-72 pl-4 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 flex-grow justify-end gap-2"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlInputButton
          type="outlined"
          dense
          label="View center details"
          :loading="submitLoading"
          @clicked="onViewCenterDetails"
        />
        <BlInputButton
          dense
          label="Go to center"
          :loading="submitLoading"
          @clicked="onGoToCenter"
        />
      </div>
      <div class="sticky right-0 z-10 pr-6 flex bg-slate-100">
        <div
          class="w-12 flex items-center flex-shrink-0 border-b border-slate-400 transition-all duration-300 border-r justify-center"
          :class="[
            isLast ? 'rounded-br' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <div class="relative">
            <BlInputButton
              type="plain"
              dense
              icon="dots-vertical"
              class="hover:bg-hover-state"
              @clicked="menuOpened = !menuOpened"
            />
            <div
              v-if="menuOpened"
              class="absolute z-50 top-0 right-full mr-3 py-2 px-2 flex flex-col gap-1 bg-pure-white rounded border border-slate-400"
            >
              <div
                v-if="center.status === 'active'"
                class="px-4 py-2 min-w-40 rounded hover:bg-hover-state cursor-pointer"
                @click="deactivateOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Deactivate center" />
              </div>
              <div
                v-if="center.status === 'deactivated'"
                class="px-4 py-2 min-w-40 rounded hover:bg-hover-state cursor-pointer"
                @click="reactivateOpened = true"
              >
                <BlTextBody2 class="text-slate-1000" text="Reactivate center" />
              </div>
              <div
                class="px-4 py-2 min-w-40 rounded hover:bg-hover-state cursor-pointer"
                :class="{
                  'pointer-events-none bg-slate-300 opacity-25': isCurrentCenter
                }"
                @click="deleteOpened = true"
              >
                <BlTextBody2 class="text-error-400" text="Delete center" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BlBackdrop
      v-if="deactivateOpened"
      :title="`Deactivate center`"
      :label="`Deactivate center`"
      type="error"
      @backdrop-close="deactivateOpened = false"
      @backdrop-action="onDeactivate"
      :submitLoading="submitLoading"
    >
      <div class="px-8">
        <div class="mb-2">
          <BlTextBody2 text="You are about to deactivate the center" />
          <BlTextBody2 :text="`${center.name}.`" class="font-bold" />
        </div>
        <div>
          <BlTextBody2
            text="By deactivating this center, all users within the center will lose access to Blüm immediately. Do you wish to proceed?"
          />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="reactivateOpened"
      :title="`Reactivate center`"
      :label="`Reactivate center`"
      @backdrop-close="reactivateOpened = false"
      @backdrop-action="onReactivate"
      :submitLoading="submitLoading"
      maxWidth="40rem"
    >
      <div class="px-8">
        <div class="mb-2">
          <BlTextBody2 text="You are about to reactivate the center" />
          <BlTextBody2 :text="`${center.name}.`" class="font-bold" />
        </div>
        <div>
          <BlTextBody2
            text="By reactivating this center, all users within the center will immediately regain access to Blüm. Do you wish to proceed?"
          />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      :title="`Delete center`"
      :label="`Delete center`"
      type="error"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
      :submitLoading="submitLoading"
    >
      <div class="px-8">
        <BlTextBody2 text="By deleting" />
        <BlTextBody2 :text="`${center.name}`" class="font-bold" />
        <BlTextBody2
          text="all data associated with it will be lost, and users will no longer be able to access the center. This action cannot be undone."
        />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "CenterItem",
  components: {
    BlTextBody2,
    BlChip,
    BlInputButton,
    BlBackdrop
  },
  props: {
    center: { type: Object, required: true },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      hoverState: false,
      menuOpened: false,
      submitLoading: false,
      deactivateOpened: false,
      reactivateOpened: false,
      deleteOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    isCurrentCenter() {
      return this.currentUser.center_id === this.center.id;
    },
    subscriptionPlan() {
      if (!this.center) return "";
      if (!this.center.current_subscription) return "";
      const plans = {
        "Subscription::Custom": "Custom",
        "Subscription::FreeTrial": "Free trial"
      };
      return plans[this.center.current_subscription.type];
    },
    nextBillingDate() {
      if (!this.center) return "";
      if (!this.center.current_subscription) return "";
      if (!this.center.current_subscription.next_billing_date) {
        return "Renewal not scheduled";
      }
      return this.displayDate({
        date: this.center.current_subscription.next_billing_date
      });
    },
    amountDue() {
      if (!this.center) return "";
      if (!this.center.current_subscription) return "";

      const settings = this.center.current_subscription.currency_settings;
      const price = this.center.current_subscription.amount;
      const amount = this.displayPrice({ settings, price });
      return `${this.center.current_subscription.currency_sym} ${amount}`;
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  async mounted() {},
  methods: {
    ...mapActions(["moveCurrentUserCenter", "updateCenter", "deleteCenter"]),
    displayDate,
    displayPrice,
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    },
    async onViewCenterDetails() {
      // const params = {
      //   center_id: this.center.id
      // };

      // this.submitLoading = true;
      // const { success } = await this.moveCurrentUserCenter(params);
      // this.submitLoading = false;
      // if (!success) return;
      this.$router.push({
        name: "SuperAdminCenter",
        params: { id: this.center.id }
      });
    },
    async onGoToCenter() {
      const params = {
        center_id: this.center.id
      };

      this.submitLoading = true;
      const { success } = await this.moveCurrentUserCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.$router.push({ name: "Clients" });
    },
    async onDeactivate() {
      // active to deactivated
      const params = {
        id: this.center.id,
        center: { status: "deactivated" },
        title: `Center deactivated`,
        message: `Success! The center has been deactivated.`
      };

      this.submitLoading = true;
      const { success } = await this.updateCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.deactivateOpened = false;
    },
    async onReactivate() {
      // deactivated to active
      const params = {
        id: this.center.id,
        center: { status: "active" },
        title: `Center activated`,
        message: `Success! The center has been activated.`
      };

      this.submitLoading = true;
      const { success } = await this.updateCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.reactivateOpened = false;
    },
    async onDelete() {
      const params = {
        id: this.center.id,
        center_name: this.center.name
      };

      this.submitLoading = true;
      const { success } = await this.deleteCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.deleteOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/centersManagement/Item.vue